import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from '@Sage/hoi-poi-ui';
import { useGoogleMap } from '@react-google-maps/api';

/** Material “my location” icon (same glyph family as Google Maps web controls). */
const MY_LOCATION_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0 0 13 3.06V1h-2v2.06A8.994 8.994 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06A8.994 8.994 0 0 0 20.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/></svg>`;

/**
 * Google Maps JS API has no built-in “my location” control (unlike the consumer Maps app).
 * Official pattern: custom control on `map.controls` — see Google’s geolocation sample.
 */
const LeadsMapOriginControl = ({ resetTarget, ariaLabel, onRecenter }) => {
    const map = useGoogleMap();
    const onRecenterRef = useRef(onRecenter);
    const [controlHost, setControlHost] = useState(null);

    onRecenterRef.current = onRecenter;

    useEffect(() => {
        if (!map || !resetTarget || !window.google?.maps?.ControlPosition) return undefined;

        const host = document.createElement('div');
        host.className = 'fm-leads-modal__map-control-host';

        const position = window.google.maps.ControlPosition.RIGHT_BOTTOM;
        const controls = map.controls[position];
        controls.push(host);
        setControlHost(host);

        return () => {
            const controlArray = controls?.getArray?.();
            const index = controlArray ? controlArray.indexOf(host) : -1;
            if (index > -1) {
                controls.removeAt(index);
            }
            setControlHost(null);
        };
    }, [map, resetTarget]);

    if (!controlHost) return null;

    return createPortal(
        <Tooltip placement="left" content={ariaLabel}>
            <button
                type="button"
                className="fm-leads-modal__map-control-button"
                aria-label={ariaLabel}
                onClick={() => onRecenterRef.current?.()}
                dangerouslySetInnerHTML={{ __html: MY_LOCATION_ICON_SVG }}
            />
        </Tooltip>,
        controlHost,
    );
};

export default LeadsMapOriginControl;
